import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLanguage } from '../i18nContext';
import StaticPageLayout from './StaticPageLayout';
import { BookOpen, Calendar } from 'lucide-react';

interface Props { onBack: () => void; }

const posts = [
  { date: '2026-02-15', titleEn: 'Introducing Incremental Document Processing', titleZh: '增量文档处理功能发布', descEn: 'Our new incremental processing engine lets you update meeting notes without re-running the entire pipeline.', descZh: '全新的增量处理引擎让您无需重新运行整个流水线即可更新会议纪要。' },
  { date: '2026-01-20', titleEn: 'Multi-Speaker Recognition Improvements', titleZh: '多说话人识别能力提升', descEn: 'We have significantly improved speaker diarization accuracy with our latest ASR model updates.', descZh: '通过最新的 ASR 模型更新，我们显著提升了说话人分离的准确率。' },
  { date: '2025-12-10', titleEn: 'Enterprise Security & Compliance Update', titleZh: '企业安全与合规更新', descEn: 'New security features including enhanced encryption, audit logs, and compliance certifications.', descZh: '新增安全功能，包括增强加密、审计日志和合规认证。' },
  { date: '2025-11-05', titleEn: 'Template System: Customize Your Meeting Notes', titleZh: '模板系统：自定义您的会议纪要', descEn: 'Create and apply custom templates to structure your meeting notes exactly the way your team needs.', descZh: '创建并应用自定义模板，按照团队需求精确构建会议纪要结构。' },
];

export default function BlogPage({ onBack }: Props) {
  const { language } = useLanguage();
  const en = language === 'en';

  return (
    <StaticPageLayout title={en ? 'Blog' : '博客'} onBack={onBack}>
      <Helmet>
        <title>{en ? 'Blog - MinutesAI' : '博客 - MinutesAI'}</title>
        <meta name="description" content={en ? 'MinutesAI blog: product updates, AI meeting intelligence insights, engineering deep-dives, and best practices for modern teams.' : 'MinutesAI 博客：产品更新、AI 会议智能洞察、工程深度解析，以及现代团队的最佳实践。'} />
        <link rel="canonical" href="https://minutesai.com/blog" />
        <meta property="og:title" content={en ? 'Blog - MinutesAI' : '博客 - MinutesAI'} />
        <meta property="og:url" content="https://minutesai.com/blog" />
      </Helmet>
      <div className="text-center mb-16">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-brand-900/10 mb-6">
          <BookOpen size={32} className="text-brand-900" />
        </div>
        <h2 className="text-4xl font-bold text-brand-900 font-serif mb-4">
          {en ? 'Blog' : '博客'}
        </h2>
        <p className="text-lg text-ink-secondary">
          {en ? 'Product updates, engineering insights, and meeting intelligence best practices.' : '产品更新、工程洞察和会议智能最佳实践。'}
        </p>
      </div>

      <div className="space-y-6">
        {posts.map((post, i) => (
          <article key={i} className="bg-white rounded-2xl border border-surface-border p-8 hover:shadow-md transition-shadow">
            <div className="flex items-center gap-2 text-sm text-ink-muted mb-3">
              <Calendar size={14} />
              <span>{post.date}</span>
            </div>
            <h3 className="text-xl font-bold text-brand-900 mb-2">{en ? post.titleEn : post.titleZh}</h3>
            <p className="text-ink-secondary">{en ? post.descEn : post.descZh}</p>
          </article>
        ))}
      </div>
    </StaticPageLayout>
  );
}
